import {
  ChangeDetectionStrategy,
  ChangeDetectorRef,
  Component,
  EventEmitter,
  OnDestroy,
  OnInit,
  Output
} from '@angular/core';
import { Subscription } from 'rxjs';
import { AppService } from '../../../app.service';
import { GlobalService } from '../../../global/services/global.service';
import { WorkspaceService } from '../../../workspace/workspace.service';
import {
  buildHeaderDataShell,
  extractObject,
  generalConfigFromGlobal,
  hydratedGlobalParams,
  normalizeFilterParams,
  rowPayloadFromActiveScreen
} from '../../../shared/workspace-element-context';
import { FirPtiSummaryReportService } from './fir-pti-summary-report.service';

import { safeJsonInput } from '../../../shared/_utils/safe-json-input';
@Component({
    selector: 'gtw-fir-pti-summary-report',
    templateUrl: './fir-pti-summary-report.component.html',
    styleUrls: ['./fir-pti-summary-report.component.scss'],
    changeDetection: ChangeDetectionStrategy.OnPush,
    standalone: false
})
export class FirPtiSummaryReportComponent implements OnInit, OnDestroy {
  @Output('request-grid-update') requestGridUpdate = new EventEmitter<string>();

  baseURLs: any = {};
  globalService: any = {};
  GENERAL_CONFIG: any = {};
  filterParams: any = {};
  rowData: any = {};

  loading = false;
  showData = false;
  pageTitle = 'PTI Summary Report - Income/(Deficit)';
  funcCurrency = '';

  columnNames: string[] = [];
  ptiTotalsData: any[] = [];
  summary: any = {};

  private isInitialized = false;
  private refreshSub?: Subscription;
  private loadEpoch = 0;
  private readonly contextSubs = new Subscription();

  constructor(
    private service: FirPtiSummaryReportService,
    private cdr: ChangeDetectorRef,
    private gtwGlobal: GlobalService,
    private gtwWorkspace: WorkspaceService,
    private gtwApp: AppService
  ) {}

  ngOnInit(): void {
    this.applyWorkspaceContext();
    this.tryInit();
    this.contextSubs.add(
      this.gtwWorkspace.setTabActive.subscribe(() => {
        this.applyWorkspaceContext();
        this.tryInit();
      })
    );
    this.contextSubs.add(
      this.gtwGlobal.globalParamsChanged$.subscribe(() => {
        this.applyWorkspaceContext();
        this.tryInit();
      })
    );
  }

  ngOnDestroy(): void {
    this.contextSubs.unsubscribe();
    this.refreshSub?.unsubscribe();
  }

  private applyWorkspaceContext(): void {
    this.isInitialized = false;
    const activeScreen = this.gtwWorkspace.workspaceFactory?.activeScreen || {};
    const shell = buildHeaderDataShell(this.gtwGlobal, this.gtwApp);
    const hydrated = hydratedGlobalParams(this.gtwGlobal, this.gtwWorkspace) || {};
    let fp = normalizeFilterParams(activeScreen);
    if (!Object.keys(fp).length) {
      fp = extractObject(hydrated);
    }
    this.filterParams = fp;
    const mergedGp = { ...extractObject(shell.globalParams), ...extractObject(hydrated) };
    this.globalService = {
      ...shell,
      globalParams: mergedGp,
      filterParams: fp,
      workspaceFactory: this.gtwWorkspace.workspaceFactory,
      inputs: this.gtwGlobal.inputs,
      sso_id: this.gtwApp?.USER_SETTINGS?.user?.sso_id ?? ''
    };
    const gc = generalConfigFromGlobal(this.gtwGlobal);
    this.GENERAL_CONFIG = { ...gc, base_url: gc.base_url || gc.api || '' };
    this.baseURLs = gc;
    const rawRow = rowPayloadFromActiveScreen(activeScreen);
    this.rowData = Array.isArray(rawRow)
      ? extractObject(rawRow[0]?.data ?? rawRow[0])
      : extractObject(rawRow);
  }

  private tryInit(): void {
    if (this.isInitialized) return;
    const baseUrl = this.GENERAL_CONFIG?.base_url || this.baseURLs?.api;
    if (!baseUrl) return;
    this.isInitialized = true;
    this.refresh();
  }

  private getParams(): any {
    return {
      fir_ho_leid: this.filterParams?.fir_ho_leid,
      filing_key: this.filterParams?.filing_key,
      me_string: this.filterParams?.me_string,
      tax_year: this.filterParams?.tax_year || this.globalService?.tax_year || this.rowData?.TAX_YEAR,
      scenario: this.filterParams?.scenario || this.globalService?.scenario || this.rowData?.SCENARIO
    };
  }

  refresh(): void {
    const baseUrl = this.GENERAL_CONFIG?.base_url || this.baseURLs?.api;
    if (!baseUrl) return;
    this.refreshSub?.unsubscribe();
    const epoch = ++this.loadEpoch;
    this.loading = true;
    this.showData = true;
    const params = this.getParams();
    const outer = this.service.getPtiSummary(baseUrl, params).subscribe({
      next: (ptiRes: any) => {
        if (epoch !== this.loadEpoch) return;
        this.parsePti(ptiRes?.jsonObject || []);
        outer.add(
          this.service.getPtepSummary(baseUrl, params).subscribe({
            next: (ptepRes: any) => {
              if (epoch !== this.loadEpoch) return;
              this.parsePtep((ptepRes?.jsonObject || [])[0] || {});
              this.loading = false;
              this.cdr.detectChanges();
            },
            error: () => {
              if (epoch !== this.loadEpoch) return;
              this.loading = false;
              this.cdr.detectChanges();
            }
          })
        );
      },
      error: () => {
        if (epoch !== this.loadEpoch) return;
        this.loading = false;
        this.cdr.detectChanges();
      }
    });
    this.refreshSub = outer;
  }

  private parsePti(rows: any[]): void {
    this.columnNames = [];
    this.ptiTotalsData = [];
    if (!rows.length) return;
    this.funcCurrency = rows[0]?.HO_FUNCTIONAL_CURRENCY || '';

    rows.forEach((r: any, idx: number) => {
      if (idx === 0) return;
      if (this.columnNames.length === 0) {
        this.columnNames = Object.keys(r || {}).filter((k) => k.toUpperCase() !== 'PTI_TYPE_TEMP');
      }
      this.ptiTotalsData.push(r);
    });

    const fx = this.safeJson(rows[0]?.GET_FX_RATE_JSON)[0] || {};
    const basis = this.safeJson(rows[0]?.GET_PTI_BASIS_TOT_AMT_JSON)[0] || {};
    const last = this.ptiTotalsData[this.ptiTotalsData.length - 1] || {};
    const totalPtiFc = Number(last?.PTI || 0);
    const fxRate = Number(fx?.END_RATE || 0);
    const ptiAmtInUSD = totalPtiFc * fxRate;
    const usdBasisInPTI = Number(basis?.TOTAL || 0);
    let estimateExchangeGainLossInUSD = usdBasisInPTI - ptiAmtInUSD;
    if (estimateExchangeGainLossInUSD < 0) estimateExchangeGainLossInUSD = estimateExchangeGainLossInUSD * -1;

    this.summary = {
      totalPtiFc,
      fxRate,
      ptiAmtInUSD,
      usdBasisInPTI,
      estimateExchangeGainLossInUSD
    };
  }

  private parsePtep(row: any): void {
    this.summary = {
      ...this.summary,
      recaptureAccounts: row?.RECAPTURE_ACCT_COUNT ?? 0,
      qualifiedDeficits: row?.QUAL_DEF_COUNT ?? 0,
      c1_ptep_total_amt: row?.C1_PTEP_TOTAL_AMT ?? 0,
      c2_ptep_total_amt: row?.C2_PTEP_TOTAL_AMT ?? 0,
      c1_c2_ptep_total_amt: row?.C1_C2_PTEP_TOTAL_AMT ?? 0
    };
  }

  private safeJson(v: any): any[] {
    try {
      if (!v) return [];
      if (Array.isArray(v)) return v;
      const p = safeJsonInput<any>(v, null, 'fir-pti-summary-report.parse1');
      return Array.isArray(p) ? p : [];
    } catch {
      return [];
    }
  }

  cancel(): void {
    this.requestGridUpdate.emit('cancel');
  }
}
